import { DragDropList } from '@/components/ui/custom/dataInput/DragDrop';
import { Loading } from '@/components/ui/daisy';
import useCourseModules from '@/data/hook/module/useModules';

import useParentResizeDnd from '@/data/hook/shared/dnd/useParentResizeDnd';
import useOptimisticReorder from '@/data/hook/useOptimisticReorder';
import { OnDragEndResponder } from '@hello-pangea/dnd';
import ModuleCard from './ModuleCard';
import ModuleFormList from './ModuleFormList';

type ModulesTabContentProps = {
  courseLineId: number;
};

export default function ModulesTabContent({
  courseLineId,
}: ModulesTabContentProps) {
  const {
    modules,
    isLoading,
    createModule,
    isCreating,
    deleteModule,
    isDeleting,
    reorderModules,
    isReordering,
  } = useCourseModules({ courseLineId });

  const { optimistic: optimisticModules, handleDragEnd: handleDragEndLocal } =
    useOptimisticReorder({
      items: modules,
      isReordering,
      onReorder: reorderModules,
    });

  const { isDraggingParent, onBeforeCapture, resetDragStyles } =
    useParentResizeDnd({ parentPrefix: 'module' });

  const handleDragEnd: OnDragEndResponder = result => {
    resetDragStyles();
    handleDragEndLocal(result);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="relative">
      <DragDropList
        groupId="modules-list"
        onDragEnd={handleDragEnd}
        onBeforeCapture={onBeforeCapture}
        disabled={isReordering}
        className="gap-0"
      >
        {optimisticModules.map((module, index) => (
          <ModuleCard
            key={module.id}
            isDragging={isDraggingParent}
            isDraggable
            module={module}
            courseLineId={courseLineId}
            onDelete={deleteModule}
            isDeleting={isDeleting}
            index={index}
            isReordering={isReordering}
          />
        ))}
      </DragDropList>
      <ModuleFormList
        modulesCount={modules.length}
        onSubmit={createModule}
        isLoading={isCreating}
      />
    </div>
  );
}
